import React, { useMemo, useState } from 'react';
import { Button, ButtonBar, Checkbox, Dialog, Section } from '@ygtechnologies/rubus-ui';
import { DirectOptionGroupDto, OptionGroupLinkStatus, ResidentialUnitExtraData } from '@/types';
import { ValidatingTextArea, Toast } from '@ygtechnologies/bygr-frontend-component-library';
import { t } from '@lingui/core/macro';
import { useChangeDirectOptionGroupStatus, useOptionGroupOrderLocks } from '@/services/options/option-groups.hooks';
import { Trans } from '@lingui/react/macro';
import { UnitSelector } from '../../UnitSelector';
import { OptionGroupStatusChangeDialogProps } from './OptionGroupStatusChangeDialog.types';
import { StatusActionLabel, StatusActionUnitsTitle } from './OptionGroupStatusChangeDialog.labels';

export const OptionGroupUnitStatusDialog: React.FC<OptionGroupStatusChangeDialogProps<DirectOptionGroupDto>> = ({
    optionGroup,
    status,
    onClose,
    onAccept,
}) => {
    const [selection, setSelection] = useState<number[]>([]);
    const [rejectReason, setRejectReason] = useState('');
    const [sendRejectEmail, setSendRejectEmail] = useState(false);
    const changeStatusMutation = useChangeDirectOptionGroupStatus();
    const { data: orderLocks } = useOptionGroupOrderLocks(optionGroup.id);

    const handleAccept = async () => {
        if (!status) {
            return;
        }

        changeStatusMutation.mutate(
            {
                optionGroupId: optionGroup.id,
                options: {
                    rejectReason: rejectReason,
                    residentialUnitIds: selection,
                    status: status!,
                    sendRejectEmail: sendRejectEmail,
                },
            },
            {
                onSuccess: () => {
                    onAccept();
                },
                onError: async (err: any) => {
                    let message = t`Unable to change status`;
                    if (err instanceof Response) {
                        try {
                            const data = await err.json();
                            message = data?.error ?? message;
                        } catch {
                            try {
                                const txt = await err.text();
                                message = txt || message;
                            } catch { }
                        }
                    }
                    Toast.error(message);
                },
            }
        );
    };

    const reset = () => {
        setSelection([]);
        setRejectReason('');
        setSendRejectEmail(false);
    };

    const filter = useMemo(() => {
        const map = new Set(optionGroup.linkedUnits.filter((x) => x.status !== status).map((x) => x.residentialUnitId));
        return (unit: ResidentialUnitExtraData) => map.has(unit.id);
    }, [optionGroup, status]);

    // A unit with an order can't be unpublished/rejected (it would orphan the order, and the backend rejects
    // it). Show it disabled/"Ordered" for those actions rather than hiding it; approving is unaffected.
    const lockedUnitIds = useMemo(
        () =>
            status === OptionGroupLinkStatus.Pending || status === OptionGroupLinkStatus.Rejected
                ? orderLocks?.unitIds ?? []
                : [],
        [status, orderLocks]
    );

    return (
        <Dialog open={status !== null} onOpenChange={(f) => onClose()} onOpen={reset}>
            <Dialog.Content contentWidth="lg">
                <Dialog.Header>
                    <h4>
                        <StatusActionUnitsTitle status={status} />
                    </h4>
                </Dialog.Header>
                {status === OptionGroupLinkStatus.Rejected && (
                    <Section variant="highlight">
                        <ValidatingTextArea
                            placeholder={t`Reject reason`}
                            className="form-control"
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                        />
                        <Checkbox
                            checked={sendRejectEmail}
                            onChange={() => setSendRejectEmail(!sendRejectEmail)}>
                            <Trans>
                                Send email notifying {optionGroup.providedByProfile?.name} about rejecting this buyer option.
                            </Trans>
                        </Checkbox>
                    </Section>
                )}

                <UnitSelector
                    projectId={optionGroup.projectId}
                    onChange={setSelection}
                    selectedResidentialUnitIds={selection}
                    lockedResidentialUnitIds={lockedUnitIds}
                    filter={filter}
                />

                <ButtonBar>
                    <ButtonBar.Right>
                        <Dialog.Close as={Button} variant="tertiary" disabled={changeStatusMutation.isPending}>
                            <Trans>Cancel</Trans>
                        </Dialog.Close>
                        <Dialog.Close
                            as={Button}
                            variant="primary"
                            onClick={handleAccept}
                            loading={changeStatusMutation.isPending}>
                            <StatusActionLabel status={status} />
                        </Dialog.Close>
                    </ButtonBar.Right>
                </ButtonBar>
            </Dialog.Content>
        </Dialog>
    );
};
